import { connectorMailReturn } from "../domain/connectorMailReturn";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Leaf,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { showDemoAccounts } from "../config/features";
import { useEms } from "../state/EmsContext";
import { useBrand } from "../state/BrandContext";

const loginSchema = z.object({
  email: z.email("Saisissez une adresse e-mail valide."),
  password: z.string().min(1, "Saisissez votre mot de passe."),
});
type LoginValues = z.infer<typeof loginSchema>;

const demoAccounts = [
  ["Super-admin", "superadmin@phebus.demo"],
  ["Energy manager principal", "manager.lead@phebus.demo"],
  ["Energy manager", "manager@phebus.demo"],
  ["Client", "client@phebus.demo"],
] as const;

function landingPath(role: "super_admin" | "manager" | "client"): string {
  return role === "super_admin" ? "/app/admin/audit" : "/app/portfolio";
}

export function LoginPage(): React.ReactNode {
  const { login, confirmTotp } = useEms();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [pendingChallengeId, setPendingChallengeId] = useState<string | null>(
    null,
  );
  const [totp, setTotp] = useState("");
  const [totpError, setTotpError] = useState("");
  const [totpBusy, setTotpBusy] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function submit(values: LoginValues): Promise<void> {
    try {
      const result = await login(values.email, values.password);
      if (result.requiresTotp) {
        setPendingChallengeId(result.challengeId ?? "");
        setTotp("");
        setTotpError("");
      } else if (result.role) {
        void navigate(
          connectorMailReturn(location.state, result.role) ??
            landingPath(result.role),
        );
      }
    } catch (error) {
      setError("root", {
        message:
          error instanceof Error ? error.message : "Connexion impossible.",
      });
    }
  }

  async function submitTotp(): Promise<void> {
    if (!pendingChallengeId) return;
    setTotpError("");
    setTotpBusy(true);
    try {
      const role = await confirmTotp(pendingChallengeId, totp);
      void navigate(
        connectorMailReturn(location.state, role) ?? landingPath(role),
      );
    } catch (error) {
      setTotpError(
        error instanceof Error ? error.message : "Vérification impossible.",
      );
    } finally {
      setTotpBusy(false);
    }
  }

  return (
    <div className="login-page">
      <section className="login-manifesto">
        <div className="login-logo" aria-label={brand.displayName}>
          <img src={brand.logoLightUrl} alt="" />
        </div>
        <div className="manifesto-copy">
          <p className="eyebrow light">ENERGY MANAGEMENT SYSTEM</p>
          <h1>
            Piloter l’énergie, c’est
            <br />
            <em>valoriser vos actifs.</em>
          </h1>
          <p>
            Mesurer. Comprendre. Agir. Une lecture opérationnelle de chaque
            site, jusqu’au point de livraison.
          </p>
        </div>
        <div className="manifesto-metrics">
          <span>
            <Leaf size={15} /> Données tracées
          </span>
          <span>
            <ShieldCheck size={15} /> Accès par périmètre
          </span>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-form-wrap">
          {pendingChallengeId !== null ? (
            <div className="totp-form">
              <span className="auth-icon">
                <LockKeyhole />
              </span>
              <p className="eyebrow">DOUBLE AUTHENTIFICATION</p>
              <h2>Vérifiez votre identité</h2>
              <p>
                Entrez le code à six chiffres de votre application
                d’authentification, ou l’un de vos codes de récupération.
              </p>
              <label htmlFor="totp">Code temporaire</label>
              <input
                id="totp"
                className="totp-input"
                value={totp}
                onChange={(event) =>
                  setTotp(event.target.value.toUpperCase().slice(0, 12))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000 000"
              />
              {totpError ? <p className="form-error">{totpError}</p> : null}
              <button
                className="primary-button full"
                onClick={() => void submitTotp()}
                disabled={totp.replace(/\s/g, "").length < 6 || totpBusy}
              >
                Vérifier <ArrowRight size={16} />
              </button>
              <button
                className="text-button"
                onClick={() => setPendingChallengeId(null)}
              >
                Utiliser un autre compte
              </button>
            </div>
          ) : (
            <>
              <p className="eyebrow">ACCÈS SÉCURISÉ</p>
              <h2>Bienvenue</h2>
              <p className="login-intro">
                Connectez-vous à votre plateforme de pilotage énergétique.
              </p>
              <form
                onSubmit={(event) => void handleSubmit(submit)(event)}
                noValidate
              >
                <label htmlFor="email">Adresse e-mail</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register("email")}
                />
                {errors.email ? (
                  <p className="form-error">{errors.email.message}</p>
                ) : null}
                <div className="password-row">
                  <label htmlFor="password">Mot de passe</label>
                  <Link to="/recovery">Accès oublié ?</Link>
                </div>
                <div className="password-input">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    {...register("password")}
                  />
                  <button
                    type="button"
                    aria-label={
                      showPassword
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {errors.password ? (
                  <p className="form-error">{errors.password.message}</p>
                ) : null}
                {errors.root ? (
                  <p className="form-error form-error-block">
                    {errors.root.message}
                  </p>
                ) : null}
                <button
                  className="primary-button full"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Connexion…" : "Se connecter"}{" "}
                  <ArrowRight size={16} />
                </button>
              </form>
              {showDemoAccounts ? (
                <details className="demo-accounts">
                  <summary>Comptes de démonstration</summary>
                  <div>
                    {demoAccounts.map(([label, email]) => (
                      <button
                        key={email}
                        onClick={() => {
                          setValue("email", email);
                          setValue("password", "demo");
                        }}
                      >
                        <span>{label}</span>
                        <code>{email}</code>
                      </button>
                    ))}
                  </div>
                </details>
              ) : null}
            </>
          )}
        </div>
        <footer>
          <span>
            <a href={brand.legalNoticeUrl}>Mentions légales</a> ·{" "}
            <a href={brand.privacyPolicyUrl}>Confidentialité</a>
          </span>
        </footer>
      </section>
    </div>
  );
}
